import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import * as React from "react";
import ReactTooltip from "react-tooltip";
import { GetEmotionColor } from "source/app/domain/Emotions/Emotions";
import { WithAnimatedProgressBarAsync } from "source/app/presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { DisplayKVLogs } from "source/app/presentation/MainPanel/KeyValueLogs/DisplayKVLogs/DisplayKVLogs";
import { EEventType } from "source/app/services/Events/EEventType";
import { DispatchEvent } from "source/app/services/Events/Events";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import { SetState } from "source/app/services/UI/State/SetState";
import { ShareState } from "source/app/services/UI/State/ShareState";
import {
    IKeyValueLog,
    resourceNameKeyValueLogs,
} from "../../../../../../motw_core/Entity/IKeyValueLog";
import { Emotions } from "../../../domain/Emotions/Emotions";
import { IKeyValueLogsState } from "./IKeyValueLogsState";
import { EmotionProgressBarContainer } from "./PercentValue/EmotionProgressBarContainer";
import { GetRecentEmotionsValuesPerDay } from "./PercentValue/GetRecentEmotionsValuesPerDay";

export function KeyValueLogs({ hidden = false }: { hidden?: boolean }) {
    const [state, setState] = React.useState<IKeyValueLogsState>({
        kvLogs: [],
        percentValue: null,
        valuePerDay_I: undefined,
        valuePerDay_P: undefined,
    });

    ShareState(ESharedFunction.KeyValueLogs_state, state, setState);

    React.useEffect(() => {
        LoadEmotionCharacterKVAsync();
    }, []);

    React.useEffect(() => {
        DispatchEvent(EEventType.KeyValueLogs_DataChanged, state.kvLogs);
    }, [state.kvLogs]);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [state.kvLogs]);

    return (
        <div
            style={{
                display: hidden ? "none" : "flex",
                flexDirection: "column",
                marginTop: "-14px",
                position: "relative",
                zIndex: 20001,
            }}
        >
            <div
                id="scrollTarget_keyValueLogs"
                style={{ textAlign: `center`, marginTop: `5px` }}
            >
                <DisplayKVLogs
                    kvLogs={state.kvLogs}
                    GetColor={(kvl) => GetEmotionColor(kvl.value)}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    margin: `6px`,
                    justifyContent: "center",
                }}
            >
                <EmotionProgressBarContainer state={state} />
            </div>
        </div>
    );
}

export async function LoadEmotionCharacterKVAsync() {
    let kvLogs: IKeyValueLog[] = [];

    try {
        const emotionsDTO = await MotwLogicServer.RecentEmotions();
        kvLogs = Array.isArray(emotionsDTO?.sourceData)
            ? emotionsDTO.sourceData
            : [];
        const percentValue =
            typeof emotionsDTO?.percentValue === "number"
                ? emotionsDTO.percentValue
                : null;
        const recentEmotionsValuesPerDay =
            GetRecentEmotionsValuesPerDay(kvLogs);

        await SetState<IKeyValueLogsState>(ESharedFunction.KeyValueLogs_state, {
            kvLogs,
            percentValue,
            valuePerDay_I: recentEmotionsValuesPerDay?.[0],
            valuePerDay_P: recentEmotionsValuesPerDay?.[1],
        });
    } catch {
        await SetState<IKeyValueLogsState>(ESharedFunction.KeyValueLogs_state, {
            kvLogs: [],
            percentValue: null,
            valuePerDay_I: undefined,
            valuePerDay_P: undefined,
        });
    }

    return kvLogs;
}

export const maxItemsToDisplay = 40;

export async function RemoveLastEmotionEntryAsync() {
    return WithAnimatedProgressBarAsync(async () => {
        // SetState schedules a React update, so reading shared state immediately
        // after a refresh can return the old (or empty) log list. Use the fetched
        // records directly to guarantee we delete the actual newest entry.
        const kvLogs = await LoadEmotionCharacterKVAsync();
        const lastId = kvLogs[0]?.id;

        if (!lastId) {
            return false;
        }

        await ClientDataProvider.DeleteRecordsAsync(
            `${resourceNameKeyValueLogs}?id=eq.${lastId}`
        );

        await LoadEmotionCharacterKVAsync();
        return true;
    });
}

export function GetEmotionValuePercentColor(percentValue: number | null) {
    if (percentValue === null) {
        return "white";
    }

    const matchingEmotion = GetMatchingEmotion(percentValue);
    return matchingEmotion.hexColor;
}

export function GetMatchingEmotion(percentValue: number) {
    if (percentValue === 100) {
        return Emotions.Integrated;
    }

    const numberOfEmotions = Emotions.LegacyAll.length;
    const emotionIndex = Math.floor((percentValue / 100) * numberOfEmotions);
    const matchingEmotion = Emotions.LegacyAll[emotionIndex];
    
    return matchingEmotion;
}
